import { useQueryClient } from "@tanstack/react-query";
import apiClient from "../api/apiClient";
import API_CONFIG from "../config/api";
import { useSelection } from "../SelectionContext";
import { useEcommercePermissions } from "../hooks/useEcommercePermissions";
import useBusinessResource from "../hooks/useBusinessResource";
import { invalidateBusinessResources } from "../utils/businessResourceFreshness";
import { parseStoreTheme } from "./storefrontSettingsModel";

export default function useStorefrontResources(storeId: number | null) {
  const { selectedCompany, selectedWarehouse } = useSelection();
  const permissions = useEcommercePermissions();
  const client = useQueryClient();
  const scope = { userId: permissions.user?.id, companyId: selectedCompany, warehouseId: selectedWarehouse, moduleScope: "ecommerce" };
  const headers = { "X-Company-Id": String(selectedCompany || "") };
  const enabled = Boolean(permissions.canViewFrontend() && permissions.canViewStores());
  const stores = useBusinessResource<any[]>({ ...scope, resource: "storefront-stores", enabled, active: true,
    load: async (signal) => {
      const rows: any[] = [];
      let page = 1;
      let total = 0;
      do {
        const { data } = await apiClient.get(API_CONFIG.apiUrl("/ecommerce/stores"), { signal, headers,
          params: { warehouse_id: selectedWarehouse, page, limit: 100 } });
        if (!Array.isArray(data.stores)) throw new Error("La liste des boutiques est indisponible.");
        // Le backend reste autoritaire ; ne jamais conserver un contexte incohérent côté formulaire.
        rows.push(...data.stores.filter((store: any) => Number(store.warehouse_id) === Number(selectedWarehouse)
          && Number(store.company_id) === Number(selectedCompany)));
        total = Number(data.pagination?.totalItems ?? data.total ?? data.stores.length);
        if (!data.stores.length) break;
        page += 1;
      } while ((page - 1) * 100 < total);
      return rows;
    },
  });
  const settings = useBusinessResource<any>({ ...scope, resource: "storefront-settings", parameters: [storeId],
    enabled: enabled && Boolean(storeId), active: true,
    load: async (signal) => {
      const { data } = await apiClient.get(API_CONFIG.apiUrl(`/ecommerce/stores/${storeId}`), { signal, headers });
      const store = data.store;
      if (!store || Number(store.id) !== storeId || Number(store.warehouse_id) !== Number(selectedWarehouse)
        || Number(store.company_id) !== Number(selectedCompany)) throw new Error("Cette boutique n'appartient plus au contexte sélectionné.");
      parseStoreTheme(store);
      return store;
    },
  });
  const catalogue = useBusinessResource<any[]>({ ...scope, resource: "storefront-preview", parameters: [storeId],
    enabled: enabled && permissions.canViewProducts() && Boolean(storeId), active: true,
    load: async (signal) => {
      const { data } = await apiClient.get(API_CONFIG.apiUrl("/produits/ecommerce"), { signal, headers, params: {
        warehouse_id: selectedWarehouse, store_id: storeId, page: 1, limit: 3, visibility: "visible", featured: "all",
      } });
      if (!data.success || !Array.isArray(data.products)) throw new Error("L'aperçu du catalogue est indisponible.");
      return data.products;
    },
  });
  return { stores, settings, catalogue, scope, headers, permissions,
    invalidate: () => invalidateBusinessResources(client, scope, "storefront-settings") };
}
